import { createClient as createWSClient } from 'graphql-ws'
import { untrack, setContext, getContext } from 'svelte'
import { throttle } from 'throttle-debounce'
import { GraphQLError } from 'graphql'

const CLIENT = Symbol('CLIENT')
const FRAGMENT_PLACEHOLDER = /\.\.\.\s*$/
const OPERATION =
  /^\s*(query|subscription|mutation|fragment)\s*(\w+)?\s*(?:(?:\([\S\s]*?\))?|on\s+\w+)\s*{/

/** @typedef {[TemplateStringsArray, (App.GraphQLOperation|string)[]]} Query */
/** @typedef {'query'|'mutation'|'subscription'|'fragment'} OperationType */

/** @type {typeof Promise.withResolvers} */
const withResolvers = () => {
  const resolvers =
    /** @type {{ resolve: (value: any) => void, reject: (reason?: any) => void }} */ ({})
  const promise = new Promise((resolve, reject) => {
    Object.assign(resolvers, { resolve, reject })
  })
  return { promise, ...resolvers }
}

/** @param {ReadonlyArray<GraphQLError | { message: string } & Partial<import('graphql').GraphQLErrorOptions>>} errors */
const asErrors = (errors) => {
  return errors.map((error) => {
    if (error instanceof Error) return error
    return new GraphQLError(error.message, error)
  })
}

/**
 * @param {{ [key: string]: any }} variables
 * @param {string} [prefix]
 */
const serialize = (variables, prefix = '') => {
  return encodeURI(
    Object.entries(variables)
      .filter(([, value]) => value != null)
      .sort(([a], [b]) => String(a).localeCompare(b))
      .map(([key, value]) => {
        key = `${prefix}${key}`
        if (typeof value === 'object' && value !== null) {
          return serialize(value, `${key}:`)
        }
        return `${key}=${value}`
      })
      .join('&')
  )
}

/** @param {Query} query */
export const compile = ([strings, values]) => {
  let external = new Set()
  const parts = strings.reduce((acc, str, i) => {
    const value = values[i]
    if (value instanceof GraphQLOperation) {
      if (value.type === 'fragment' && FRAGMENT_PLACEHOLDER.test(str)) {
        external.add(value.query)
        acc.push(str, value.name)
      } else {
        acc.push(str, compile(value.query))
      }
    } else {
      acc.push(str)
    }
    return acc
  }, /** @type {string[]} */ ([]))

  return (
    parts.join('') +
    (external.size
      ? `\n${[...external].map((query) => compile(query)).join('\n')}`
      : '')
  )
}

/** @param {{ url: string|URL, headers?: Headers|{ [key: string]: string } }} opts */
export function setGraphQLContext(opts) {
  setContext(CLIENT, new Client(opts))
}

export class Client {
  /** type {string} */
  #url

  /** type {Headers|{ [key: string]: string }} */
  #headers

  /** @type {import('graphql-ws').Client?} */
  #wsClient

  /** @type {WeakMap<TemplateStringsArray, { [key: string]: any }>} */
  #cache = new WeakMap()

  /** @param {{ url: string|URL, headers?: Headers|{ [key: string]: string } }} opts */
  constructor({ url, headers }) {
    this.#headers = headers
    this.#url = url
    this.#wsClient =
      typeof WebSocket !== 'undefined'
        ? createWSClient({
            lazyCloseTimeout: 10000,
            url: String(url).replace(/^http/, 'ws'),
            // @ts-ignore
            connectionParams: headers
          })
        : null
  }

  /**
   * @param {GraphQLOperation} operation
   * @param {{ [key: string]: any }} variables
   */
  read(operation, variables) {
    const [key] = operation.query
    const cache = this.#cache.get(key)
    return cache?.[serialize(variables)]
  }

  /**
   * @param {GraphQLOperation} operation
   * @param {{ [key: string]: any }} variables
   * @param {any} body
   */
  write(operation, variables, body) {
    const [key] = operation.query
    const cache = this.#cache.get(key)
    if (!cache) {
      this.#cache.set(key, { [serialize(variables)]: body })
    } else {
      cache[serialize(variables)] = body
    }
  }

  clear(operation, variables) {
    const [key] = operation.query
    const cache = this.#cache.get(key)
    if (cache) delete cache[serialize(variables)]
  }

  /**
   * @template {any} Data
   * @template {{ [key: string]: any }} Variables
   * @param {GraphQLOperation<Data, Variables>} operation
   * @param {Variables} variables
   * @returns {Promise<Data|undefined>}
   */
  async fetch(operation, variables) {
    const useCache = operation.type === 'query'
    if (useCache) {
      const cached = this.read(operation, variables)
      if (cached) return /** @type {Data} */ (cached)
    }

    const promise = fetch(this.#url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.#headers
      },
      body: JSON.stringify({ query: compile(operation.query), variables })
    })
      .then(async (res) => {
        /** @type {{ data?: Data, errors?: import('graphql').GraphQLError[] }} */
        const { data, errors } = await res.json()

        if (errors) throw new AggregateError(asErrors(errors), 'GraphQL error')

        if (useCache) {
          this.write(operation, variables, data)
        }

        return data
      })
      .catch((err) => {
        this.clear(operation, variables)
        throw err
      })

    if (useCache) {
      this.write(operation, variables, promise)
    }

    return promise
  }

  /**
   * @template {any} Data
   * @template {{ [key: string]: any }} Variables
   * @param {GraphQLOperation<Data, Variables>} operation
   * @param {Variables} variables
   * @param {import('graphql-ws').Sink<App.ExecutionResult<Data>>} sink
   */
  subscribe(operation, variables, sink) {
    if (!this.#wsClient) {
      throw new Error('WebSockets not supported in this environment')
    }

    return this.#wsClient.subscribe(
      { query: compile(operation.query), variables },
      sink
    )
  }
}

/**
 * @template {any} Data
 * @template {{ [key: string]: any }} Variables
 * @type {App.GraphQLOperation<Data, Variables>}
 */
export class GraphQLOperation {
  /** @type {Data|undefined} */
  data = $state()

  /** @type {Error|undefined} */
  error = $state()

  stale = $state(false)

  fetching = $state(false)

  /** @type {Promise<Data|undefined>|undefined} */
  promise = $state()

  /** @type {Client|undefined} */
  #client

  /** @type {OperationType} */
  type

  /** @type {string} */
  name

  /** @type [TemplateStringsArray, any[]] */
  query

  /**
   * @param {Query} query
   * @param {Client} [client]
   */
  constructor(query, client) {
    const [strings] = query
    const [, type, name] = strings[0].match(OPERATION) || []
    if (!type) throw new Error('Invalid operation')

    this.query = query
    this.type = /** @type {OperationType} */ (type)
    this.name = name || `${type}_${Math.random().toString(36).substring(2)}`

    try {
      this.#client = client || getContext(CLIENT)
    } catch (err) {
      // Allow queries being created ourside component initialization
    }
  }

  /**
   * @param {Client} [client]
   * @returns {App.GraphQLOperation<Data, Variables>}
   */
  create(client = this.#client) {
    return new GraphQLOperation(this.query, client)
  }

  /**
   * Utility method for use with $effect rune to prevent reactive loops
   * @param {Variables} [variables]
   */
  execute(variables = /** @type {Variables} */ ({})) {
    return untrack(() => this.#execute(variables))
  }

  /** @param {Variables} variables */
  #execute(variables) {
    const client = this.#client
    if (!client) {
      throw new Error('Client not provided')
    }

    let cancelled = false
    const cancel = () => {
      cancelled = true
    }

    /** @param {App.ExecutionResult<Data, any>} next */
    const update = ({ data, errors }) => {
      this.error = errors
        ? new AggregateError(asErrors(errors), 'GraphQL error')
        : undefined
      this.data = data || undefined
      this.stale = false
    }

    const cached = client.read(this, variables)
    if (cached) {
      if (cached instanceof Promise) {
        this.fetching = true
        this.promise = Promise.resolve(cached).then((data) => {
          if (cancelled) return
          this.fetching = false
          update({ data })
          return data
        })
      } else {
        update({ data: cached })
        this.promise = Promise.resolve(cached)
        this.fetching = false
      }
      return cancel
    }

    /** @type {App.ExecutionResult<Data, any>} */
    let payload = {}
    const throttled = throttle(400, update)
    const { resolve, reject, promise } = withResolvers()

    this.fetching = true
    this.promise = promise
    this.stale = Boolean(this.data || this.error)

    if (this.type === 'subscription') {
      const unsubscribe = client.subscribe(this, variables, {
        next(next) {
          if (cancelled) return
          throttled(next)
          payload = next
        },
        error: (err) => {
          if (cancelled) return
          if (Array.isArray(err)) {
            this.error = new AggregateError(asErrors(err), 'Connection error')
          } else if (err instanceof Error) {
            this.error = err
          } else if (err && typeof err === 'object' && 'error' in err) {
            this.error = /** @type {Error} */ (err.error)
          } else if (err && typeof err === 'object' && 'reason' in err) {
            this.error = new Error(String(err.reason))
          } else {
            this.error = new Error(String(err))
          }
          this.fetching = false
          this.stale = false
          reject(this.error)
        },
        complete: () => {
          if (cancelled) return
          this.fetching = false
          // @ts-ignore
          throttled.cancel()
          update(payload)
          resolve(payload.data)
        }
      })

      return () => {
        // @ts-ignore
        throttled.cancel()
        unsubscribe()
        cancel()
      }
    } else if (this.type === 'mutation' || this.type === 'query') {
      client
        .fetch(this, variables)
        .then(
          (data) => ({ data, error: undefined }),
          (error) => ({ error, data: undefined })
        )
        .then(({ data, error }) => {
          if (cancelled) return
          this.fetching = false
          this.stale = false
          this.error = error
          this.data = data
          if (error) reject(error)
          else resolve(data)
        })

      return cancel
    }

    this.error = new Error(`Cannot execute ${this.type}`)
    reject(this.error)
    return cancel
  }
}

/**
 * @template {any} Data
 * @template {{ [key: string]: any }} Variables
 * @param {TemplateStringsArray} strings
 * @param {(App.GraphQLOperation|string)[]} values
 * @returns {App.GraphQLOperation<Data, Variables>}
 */
export function gql(strings, ...values) {
  return new GraphQLOperation([strings, values])
}
